import React, { useEffect, useState } from 'react';
import { meetingsAPI, usersAPI } from '../../services/api';
import { parseApiError } from '../../utils/errors';
import { fmtDate } from '../../utils/dateUtils';

const STATUS_COLOR = { scheduled: 'blue', completed: 'green', cancelled: 'gray' };

export default function WeeklyMeeting() {
  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', week_start: '', meeting_datetime: '', agenda: '', attendee_ids: '', meeting_link: '' });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => meetingsAPI.list().then(({ data }) => setMeetings(data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    usersAPI.list({ is_active: true }).then(({ data }) => setEmployees(data.filter(u => u.role !== 'super_admin')));
  }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const toggleAttendee = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Please enter a meeting title.'); return; }
    if (!form.week_start) { setError('Please select the week starting date.'); return; }
    if (!form.meeting_datetime) { setError('Please select the meeting date and time.'); return; }
    if (selected.length === 0) { setError('Please select at least one attendee.'); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        attendee_ids: selected.join(','),
        meeting_datetime: new Date(form.meeting_datetime).toISOString(),
      };
      await meetingsAPI.create(payload);
      setShowForm(false);
      setForm({ title: '', week_start: '', meeting_datetime: '', agenda: '', attendee_ids: '', meeting_link: '' });
      setSelected([]);
      setSuccess('Meeting scheduled successfully.');
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (err) {
      setError(parseApiError(err, 'Failed to schedule meeting. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await meetingsAPI.update(id, { status });
      load();
    } catch (err) {
      setError(parseApiError(err, 'Failed to update meeting.'));
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Weekly Review Meetings</h2>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setError(''); }}>{showForm ? 'Cancel' : '+ Schedule Meeting'}</button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="form-card mb-6">
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Meeting Title *</label>
                <input required value={form.title} onChange={set('title')} />
              </div>
              <div className="form-group">
                <label>Week Starting *</label>
                <input type="date" required value={form.week_start} onChange={set('week_start')} />
              </div>
            </div>
            <div className="form-group">
              <label>Meeting Date &amp; Time</label>
              <input type="datetime-local" value={form.meeting_datetime} onChange={set('meeting_datetime')} />
              <small className="field-hint">Set the exact date and time for the meeting</small>
            </div>
            <div className="form-group">
              <label>Agenda</label>
              <textarea rows={3} value={form.agenda} onChange={set('agenda')} placeholder="Meeting agenda..." />
            </div>
            <div className="form-group">
              <label>Meeting Link</label>
              <input value={form.meeting_link} onChange={set('meeting_link')} placeholder="https://meet.google.com/..." />
            </div>
            <div className="form-group">
              <label>Attendees</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {employees.map((e) => (
                  <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleAttendee(e.id)} />
                    {e.full_name}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Scheduling…' : 'Schedule Meeting'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="page-loading">Loading...</div> : meetings.length === 0 ? (
        <div className="empty-state">No meetings scheduled.</div>
      ) : (
        <div className="concern-list">
          {meetings.map((m) => (
            <div key={m.id} className="concern-card">
              <div className="concern-header">
                <div>
                  <h4>{m.title}</h4>
                  <small className="text-muted">Week of {m.week_start}</small>
                  {m.meeting_datetime && (
                    <div style={{ fontSize: 12, color: '#2563eb', marginTop: 2 }}>
                      📅 {fmtDate(m.meeting_datetime)}
                    </div>
                  )}
                </div>
                <div className="flex-col-end">
                  <span className={`badge badge-${STATUS_COLOR[m.status]}`}>{m.status}</span>
                  {m.status === 'scheduled' && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button className="btn btn-sm btn-success" onClick={() => updateStatus(m.id, 'completed')}>Complete</button>
                      <button className="btn btn-sm btn-danger" onClick={() => updateStatus(m.id, 'cancelled')}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
              {m.agenda && <p className="concern-content">{m.agenda}</p>}
              {m.meeting_link && <a href={m.meeting_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-ghost mt-2">Join Meeting</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
